// Comprehensive Confidence Filtering Architecture
// Task #9: Define Confidence Filtering Architecture

import { PayrollCase } from './index';

// Enhanced confidence thresholds with detailed routing rules
export const CONFIDENCE_ARCHITECTURE = {
  // Core thresholds
  THRESHOLDS: {
    MINIMUM_VISIBILITY: 85, // Cases below this are hidden from CSRs
    AUTO_APPROVAL: 95, // Cases that can be auto-processed
    HIGH_CONFIDENCE: 90, // High confidence requiring minimal review
    MEDIUM_CONFIDENCE: 75, // Medium confidence requiring standard review
    LOW_CONFIDENCE: 60, // Low confidence requiring enhanced review
    CRITICAL_THRESHOLD: 50, // Below this triggers immediate escalation
  },

  // Routing decisions based on confidence levels
  ROUTING_RULES: {
    AUTO_PROCESS: {
      threshold: 95,
      action: 'auto_process',
      description: 'Automatically process without CSR intervention',
      csrReview: false,
      escalation: false,
      maxReviewTime: '2_minutes',
      additionalValidation: false,
    },
    HIGH_CONFIDENCE: {
      threshold: 90,
      action: 'quick_review',
      description: 'Quick CSR review and approve',
      csrReview: true,
      escalation: false,
      maxReviewTime: '5_minutes',
      additionalValidation: false,
    },
    MEDIUM_CONFIDENCE: {
      threshold: 75,
      action: 'standard_review',
      description: 'Standard CSR review and validation',
      csrReview: true,
      escalation: false,
      maxReviewTime: '15_minutes',
      additionalValidation: false,
    },
    LOW_CONFIDENCE: {
      threshold: 60,
      action: 'enhanced_review',
      description: 'Enhanced CSR review with additional validation',
      csrReview: true,
      escalation: false,
      maxReviewTime: '30_minutes',
      additionalValidation: true,
    },
    CRITICAL_LOW: {
      threshold: 50,
      action: 'immediate_escalation',
      description: 'Immediate escalation to supervisor',
      csrReview: false,
      escalation: true,
      escalationType: 'immediate',
      maxReviewTime: 'immediate',
      additionalValidation: false,
    },
  },

  // Flagging mechanisms for different scenarios
  FLAGGING_RULES: {
    BELOW_VISIBILITY: {
      condition: 'confidence < 85',
      action: 'flag_to_dynamics',
      priority: 'high',
      message: 'Case confidence below visibility threshold',
      reprocessing: true,
    },
    PROCESSING_ERROR: {
      condition: 'processing_failed',
      action: 'flag_to_dynamics',
      priority: 'critical',
      message: 'Case processing failed - requires manual review',
      reprocessing: false,
    },
    DATA_QUALITY: {
      condition: 'data_integrity_failed',
      action: 'flag_to_dynamics',
      priority: 'high',
      message: 'Data quality issues detected',
      reprocessing: true,
    },
  },
} as const;

// Enhanced confidence level with routing information
export type DetailedConfidenceLevel =
  | 'auto_process'
  | 'high'
  | 'medium'
  | 'low'
  | 'critical';

// Confidence assessment result
export interface ConfidenceAssessment {
  score: number;
  level: DetailedConfidenceLevel;
  routing: (typeof CONFIDENCE_ARCHITECTURE.ROUTING_RULES)[keyof typeof CONFIDENCE_ARCHITECTURE.ROUTING_RULES];
  isVisible: boolean;
  requiresFlag: boolean;
  flagReason?: string;
  estimatedProcessingTime: string;
  additionalValidation: boolean;
}

// Flagging information for Dynamics
export interface CaseFlagging {
  caseId: string;
  reason: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  confidence: number;
  flagType: 'reprocessing' | 'escalation' | 'data_quality';
  metadata: {
    originalConfidence: number;
    processingAttempts: number;
    lastError?: string;
  };
}

// Quality metrics for confidence tracking
export interface ConfidenceMetrics {
  totalCases: number;
  averageConfidence: number;
  distributionByLevel: {
    auto_process: number;
    high: number;
    medium: number;
    low: number;
    critical: number;
  };
  flaggedCases: number;
  flaggedPercentage: number;
  accuracyRate: number; // How often high-confidence cases succeed
}

// Enhanced confidence evaluation function
export const evaluateConfidence = (
  confidence: number,
  additionalFactors?: {
    processingAttempts?: number;
    dataQualityScore?: number;
    documentComplexity?: number;
  }
): ConfidenceAssessment => {
  // Apply additional factors to confidence score
  let adjustedConfidence = confidence;

  if (additionalFactors) {
    // Reduce confidence for multiple processing attempts
    if (
      additionalFactors.processingAttempts &&
      additionalFactors.processingAttempts > 1
    ) {
      adjustedConfidence -= (additionalFactors.processingAttempts - 1) * 5;
    }

    // Adjust for data quality
    if (additionalFactors.dataQualityScore !== undefined) {
      adjustedConfidence =
        (adjustedConfidence + additionalFactors.dataQualityScore) / 2;
    }

    // Adjust for document complexity
    if (additionalFactors.documentComplexity !== undefined) {
      const complexityPenalty = additionalFactors.documentComplexity > 0.7 ? 10 : 0;
      adjustedConfidence -= complexityPenalty;
    }
  }

  // Ensure confidence stays within valid range
  adjustedConfidence = Math.max(0, Math.min(100, adjustedConfidence));

  // Determine routing based on adjusted confidence
  let routing;
  let level: DetailedConfidenceLevel;

  if (adjustedConfidence >= CONFIDENCE_ARCHITECTURE.THRESHOLDS.AUTO_APPROVAL) {
    routing = CONFIDENCE_ARCHITECTURE.ROUTING_RULES.AUTO_PROCESS;
    level = 'auto_process';
  } else if (adjustedConfidence >= CONFIDENCE_ARCHITECTURE.THRESHOLDS.HIGH_CONFIDENCE) {
    routing = CONFIDENCE_ARCHITECTURE.ROUTING_RULES.HIGH_CONFIDENCE;
    level = 'high';
  } else if (
    adjustedConfidence >= CONFIDENCE_ARCHITECTURE.THRESHOLDS.MEDIUM_CONFIDENCE
  ) {
    routing = CONFIDENCE_ARCHITECTURE.ROUTING_RULES.MEDIUM_CONFIDENCE;
    level = 'medium';
  } else if (adjustedConfidence >= CONFIDENCE_ARCHITECTURE.THRESHOLDS.LOW_CONFIDENCE) {
    routing = CONFIDENCE_ARCHITECTURE.ROUTING_RULES.LOW_CONFIDENCE;
    level = 'low';
  } else {
    routing = CONFIDENCE_ARCHITECTURE.ROUTING_RULES.CRITICAL_LOW;
    level = 'critical';
  }

  const isVisible =
    adjustedConfidence >= CONFIDENCE_ARCHITECTURE.THRESHOLDS.MINIMUM_VISIBILITY;
  const requiresFlag = !isVisible || level === 'critical';

  return {
    score: adjustedConfidence,
    level,
    routing,
    isVisible,
    requiresFlag,
    flagReason: requiresFlag
      ? isVisible
        ? 'Critical confidence level'
        : 'Below visibility threshold'
      : undefined,
    estimatedProcessingTime: routing.maxReviewTime || 'immediate',
    additionalValidation: routing.additionalValidation || false,
  };
};

// Function to generate flagging information
export const generateCaseFlagging = (
  caseItem: PayrollCase,
  assessment: ConfidenceAssessment,
  reason: string
): CaseFlagging => {
  return {
    caseId: caseItem.caseNumber,
    reason,
    priority: assessment.level === 'critical' ? 'critical' : 'high',
    timestamp: new Date().toISOString(),
    confidence: assessment.score,
    flagType: assessment.level === 'critical' ? 'escalation' : 'reprocessing',
    metadata: {
      originalConfidence: caseItem.aiConfidence,
      processingAttempts: 1,
      lastError:
        assessment.level === 'critical' ? 'Critical confidence level' : undefined,
    },
  };
};

// Function to calculate confidence metrics
export const calculateConfidenceMetrics = (cases: PayrollCase[]): ConfidenceMetrics => {
  const assessments = cases.map((caseItem) =>
    evaluateConfidence(caseItem.aiConfidence)
  );

  const distribution = {
    auto_process: 0,
    high: 0,
    medium: 0,
    low: 0,
    critical: 0,
  };

  let totalConfidence = 0;
  let flaggedCases = 0;

  assessments.forEach((assessment) => {
    distribution[assessment.level]++;
    totalConfidence += assessment.score;
    if (assessment.requiresFlag) flaggedCases++;
  });

  return {
    totalCases: cases.length,
    averageConfidence: cases.length > 0 ? totalConfidence / cases.length : 0,
    distributionByLevel: distribution,
    flaggedCases,
    flaggedPercentage: cases.length > 0 ? (flaggedCases / cases.length) * 100 : 0,
    accuracyRate: 0, // This would be calculated based on historical success rates
  };
};

// Validation rules for business logic
export const BUSINESS_VALIDATION_RULES = {
  MAX_HOURS_PER_WEEK: 80,
  MAX_HOURS_PER_DAY: 24,
  REQUIRED_FIELDS: ['employeeName', 'hoursWorked', 'payRate'],
  VALID_PAY_RATES: { min: 7.25, max: 200 }, // Federal minimum to reasonable maximum
  VALID_HOUR_INCREMENTS: [0.25, 0.5, 0.75, 1.0], // Quarter-hour increments
} as const;

// Function to validate business rules and adjust confidence
export const validateBusinessRules = (
  payrollData: any
): {
  isValid: boolean;
  violations: string[];
  confidencePenalty: number;
} => {
  const violations: string[] = [];
  let confidencePenalty = 0;

  // Check required fields
  BUSINESS_VALIDATION_RULES.REQUIRED_FIELDS.forEach((field) => {
    if (!payrollData[field]) {
      violations.push(`Missing required field: ${field}`);
      confidencePenalty += 10;
    }
  });

  // Check hours validity
  if (payrollData.hoursWorked > BUSINESS_VALIDATION_RULES.MAX_HOURS_PER_WEEK) {
    violations.push('Hours exceed maximum per week');
    confidencePenalty += 15;
  }

  // Check pay rate validity
  if (
    payrollData.payRate < BUSINESS_VALIDATION_RULES.VALID_PAY_RATES.min ||
    payrollData.payRate > BUSINESS_VALIDATION_RULES.VALID_PAY_RATES.max
  ) {
    violations.push('Pay rate outside valid range');
    confidencePenalty += 20;
  }

  return {
    isValid: violations.length === 0,
    violations,
    confidencePenalty,
  };
};
